import type { ReactNode } from 'react';
import type {
  FieldType,
  FormField,
  FormSchemaV2,
  FormSection,
  SectionKind,
} from '../../lib/agentShared/formSchema';
import { FIELD_TYPES } from '../../lib/agentShared/formSchema';
import { TYPE_LABELS } from '../formBuilder/editorParts';
import {
  Badge,
  ContextHeader,
  DataList,
  Input,
  Select,
  Stack,
  Surface,
  Switch,
  Textarea,
} from '../ui';
import type { TemplateEditorSelection } from './TemplateEditorOutline';

export interface TemplateEditorInspectorProps {
  schema: FormSchemaV2;
  selection: TemplateEditorSelection;
  disabled?: boolean;
  onTemplateChange: (patch: { title?: string; description?: string | undefined }) => void;
  onSectionChange: (sectionId: string, patch: TemplateEditorPatch<FormSection>) => void;
  onFieldChange: (fieldId: string, patch: TemplateEditorPatch<FormField>) => void;
}

export type TemplateEditorPatch<T> = { [Key in keyof T]?: T[Key] | undefined };

function PropertyField({
  label,
  description,
  children,
}: {
  label: string;
  description?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <Stack as="label" gap={1} className="min-w-0">
      <span className="text-label font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
      {children}
      {description && <span className="break-words text-caption leading-relaxed text-muted-foreground">{description}</span>}
    </Stack>
  );
}

const sectionKindLabel: Record<SectionKind, string> = {
  section: 'Section',
  page: 'Page',
  table: 'Table',
};

export function TemplateEditorInspector({
  schema,
  selection,
  disabled = false,
  onTemplateChange,
  onSectionChange,
  onFieldChange,
}: TemplateEditorInspectorProps): JSX.Element {
  const selectedSection = selection.kind === 'template'
    ? undefined
    : schema.sections.find((section) => section.id === selection.sectionId);
  const selectedField = selection.kind === 'field'
    ? selectedSection?.fields.find((field) => field.id === selection.fieldId)
    : undefined;
  const questionCount = schema.sections.reduce((total, section) => total + section.fields.length, 0);

  let selectionTitle = schema.title || 'Untitled template';
  let selectionSubtitle = `${schema.sections.length} sections · ${questionCount} fields`;
  let properties: ReactNode = (
    <>
      <PropertyField label="Template name">
        <Input
          className="w-full min-w-0"
          value={schema.title}
          disabled={disabled}
          onChange={(event) => onTemplateChange({ title: event.target.value })}
        />
      </PropertyField>

      <PropertyField label="Description" description="Explain what this template covers and when it should be used.">
        <Textarea
          className="w-full min-w-0"
          rows={4}
          value={schema.description ?? ''}
          disabled={disabled}
          onChange={(event) => onTemplateChange({ description: event.target.value || undefined })}
        />
      </PropertyField>

      <DataList
        density="compact"
        items={[
          { label: 'Sections', value: schema.sections.length },
          { label: 'Fields', value: questionCount },
          { label: 'Relations', value: schema.relations.length },
        ]}
      />

      <p className="break-words text-caption leading-relaxed text-muted-foreground">
        Select a section in the outline or a question on the canvas to edit its focused settings.
      </p>
    </>
  );

  if (selection.kind === 'section' && selectedSection) {
    selectionTitle = selectedSection.name || 'Untitled section';
    selectionSubtitle = `${sectionKindLabel[selectedSection.kind ?? 'section']} · ${selectedSection.fields.length} fields`;
    properties = (
      <>
        <PropertyField label="Section name">
          <Input
            className="w-full min-w-0"
            value={selectedSection.name}
            disabled={disabled}
            onChange={(event) => onSectionChange(selectedSection.id, { name: event.target.value })}
          />
        </PropertyField>

        <PropertyField label="Structure" description="Pages create a report break; tables use questions as columns.">
          <Select
            className="w-full min-w-0"
            value={selectedSection.kind ?? 'section'}
            disabled={disabled}
            onChange={(event) => onSectionChange(selectedSection.id, { kind: event.target.value as SectionKind })}
          >
            {(Object.keys(sectionKindLabel) as SectionKind[]).map((kind) => (
              <option key={kind} value={kind}>{sectionKindLabel[kind]}</option>
            ))}
          </Select>
        </PropertyField>

        <Switch
          value={Boolean(selectedSection.repeat)}
          disabled={disabled}
          label="Repeat this section"
          onChange={(repeat) => onSectionChange(selectedSection.id, { repeat })}
        />

        <PropertyField label="Description">
          <Textarea
            className="w-full min-w-0"
            rows={3}
            value={selectedSection.description ?? ''}
            disabled={disabled}
            onChange={(event) => onSectionChange(selectedSection.id, { description: event.target.value || undefined })}
          />
        </PropertyField>

        <DataList
          density="compact"
          items={[
            { label: 'Fields', value: selectedSection.fields.length },
            { label: 'Conditions', value: selectedSection.visibleWhen?.length ?? 0 },
          ]}
        />
      </>
    );
  }

  if (selection.kind === 'field' && selectedSection && selectedField) {
    const isChoice = selectedField.type === 'select' || selectedField.type === 'multiselect';
    const isNumeric = selectedField.type === 'number' || selectedField.type === 'rating';
    const patchNumber = (key: 'min' | 'max' | 'step', value: string) => {
      onFieldChange(selectedField.id, { [key]: value === '' ? undefined : Number(value) });
    };

    selectionTitle = selectedField.label || 'Untitled question';
    selectionSubtitle = `${TYPE_LABELS[selectedField.type]} · ${selectedSection.name}`;
    properties = (
      <>
        <PropertyField label="Question label">
          <Input
            className="w-full min-w-0"
            value={selectedField.label}
            disabled={disabled}
            onChange={(event) => onFieldChange(selectedField.id, { label: event.target.value })}
          />
        </PropertyField>

        <PropertyField label="Response type">
          <Select
            className="w-full min-w-0"
            value={selectedField.type}
            disabled={disabled}
            onChange={(event) => {
              const type = event.target.value as FieldType;
              const patch: TemplateEditorPatch<FormField> = { type };
              if ((type === 'select' || type === 'multiselect') && (selectedField.options?.length ?? 0) === 0) {
                patch.options = [{ label: 'Option 1' }, { label: 'Option 2' }];
              }
              onFieldChange(selectedField.id, patch);
            }}
          >
            {FIELD_TYPES.map((type) => (
              <option key={type} value={type}>{TYPE_LABELS[type]}</option>
            ))}
          </Select>
        </PropertyField>

        <Switch
          value={Boolean(selectedField.required)}
          disabled={disabled}
          label="Required response"
          onChange={(required) => onFieldChange(selectedField.id, { required })}
        />

        <PropertyField label="Description" description="Shown during inspection as supporting guidance.">
          <Textarea
            className="w-full min-w-0"
            rows={3}
            value={selectedField.description ?? ''}
            disabled={disabled}
            onChange={(event) => onFieldChange(selectedField.id, { description: event.target.value || undefined })}
          />
        </PropertyField>

        <PropertyField label="Field key" description="Logic and report mappings use this stable key.">
          <Input
            className="w-full min-w-0"
            value={selectedField.key}
            disabled={disabled}
            onChange={(event) => onFieldChange(selectedField.id, { key: event.target.value })}
          />
        </PropertyField>

        {isChoice && (
          <Stack gap={1}>
            <span className="text-label font-semibold uppercase tracking-wide text-muted-foreground">Responses</span>
            <Badge tone="neutral">{selectedField.options?.length ?? 0} configured</Badge>
            <p className="break-words text-caption leading-relaxed text-muted-foreground">
              Labels, scores and flagged values remain editable from the selected question row.
            </p>
          </Stack>
        )}

        {isNumeric && (
          <Stack gap={3}>
            <PropertyField label="Minimum">
              <Input
                className="w-full min-w-0"
                type="number"
                value={selectedField.min ?? ''}
                disabled={disabled}
                onChange={(event) => patchNumber('min', event.target.value)}
              />
            </PropertyField>
            <PropertyField label="Maximum">
              <Input
                className="w-full min-w-0"
                type="number"
                value={selectedField.max ?? ''}
                disabled={disabled}
                onChange={(event) => patchNumber('max', event.target.value)}
              />
            </PropertyField>
            <PropertyField label="Step">
              <Input
                className="w-full min-w-0"
                type="number"
                value={selectedField.step ?? ''}
                disabled={disabled}
                onChange={(event) => patchNumber('step', event.target.value)}
              />
            </PropertyField>
          </Stack>
        )}

        <DataList
          density="compact"
          items={[
            { label: 'Conditions', value: selectedField.visibleWhen?.length ?? 0 },
            { label: 'Requirements', value: selectedField.requirements?.length ?? 0 },
          ]}
        />
      </>
    );
  }

  return (
    <Surface
      as="section"
      variant="bare"
      className="min-h-full"
      data-testid="template-editor-inspector"
    >
      <Surface as="section" variant="flat" padding="sm" className="rounded-none border-x-0 border-t-0">
        <ContextHeader title="Settings" headingLevel={2} />
      </Surface>
      <Stack gap={4} className="min-w-0 p-4">
        <Stack gap={0.5} className="min-w-0">
          <h3 className="break-words text-sm font-semibold text-foreground">{selectionTitle}</h3>
          <p className="break-words text-caption leading-relaxed text-muted-foreground">{selectionSubtitle}</p>
        </Stack>
        {properties}
      </Stack>
    </Surface>
  );
}
